import { EventSystem } from "@/utils/event/eventSystem";
import { v4 } from "uuid";

export type IStackItem<T extends Record<PropertyKey, any> = {}> = T & {
  id: string;
};

export type IStackEntry<T extends Record<PropertyKey, any> = {}> = {
  item: IStackItem<T>;
  id: string;
  index: number;
  __v_skip: true;
};

export type UseIDStack<T extends Record<PropertyKey, any> = {}> = {
  push: (item: T) => IStackEntry<T>;
  pop: (id?: string) => Promise<void>;
  clear: () => Promise<void>;
  items: ComputedRef<Array<IStackItem<T>>>;
  events: EventSystem<IDStackEventMap>;
};

export enum EIDStackEvent {
  PUSH = "PUSH",
  POP = "POP",
  CLEAR = "CLEAR",
  REMOVED = 'REMOVED'
}

export type IDStackEventMap = {
  [EIDStackEvent.PUSH]: {
    eventType: EIDStackEvent.PUSH;
    entry: IStackEntry;
    currentLength: number;
  };
  [EIDStackEvent.POP]: {
    eventType: EIDStackEvent.POP;
    id: string;
    currentLength: number;
  };
  [EIDStackEvent.CLEAR]: {
    eventType: EIDStackEvent.CLEAR;
  };
  [EIDStackEvent.REMOVED]: {
    eventType: EIDStackEvent.REMOVED;
    id: string;
    currentLength: number;
  };
};

export const useIDStack = <
  T extends Record<PropertyKey, any>,
>(): UseIDStack<T> => {
  const indexCounter: number = 0;
  const data = ref<Map<string, IStackEntry<T>>>(new Map());
  const sortedItems = computed(() =>
    Array.from(data.value.values()).sort((a, b) => a.index - b.index),
  );
  const outputItems = computed(() => sortedItems.value.map((it) => it.item));
  const events = new EventSystem<IDStackEventMap>();

  const clear = async () => {
    for (const item of data.value.values()) {
      await events.emitAsync({ eventType: EIDStackEvent.REMOVED, id: item.id, currentLength: 0 });
    }
    data.value.clear();
    await events.emitAsync({ eventType: EIDStackEvent.CLEAR });
  }

  const push = (item: T) => {
    const id = v4();
    const index = indexCounter;
    const entry: IStackEntry<T> = {
      item: { ...item, id },
      id,
      index,
      __v_skip: true,
    };
    data.value.set(id, entry as unknown as any);
    events.emit({
      eventType: EIDStackEvent.PUSH,
      entry,
      currentLength: data.value.size,
    });
    return entry;
  };

  const pop = async (idToPop?: string) => {
    const id = idToPop || sortedItems.value[sortedItems.value.length - 1]?.id;
    if (!id) return;
    const currentLength = data.value.size - 1;
    await events.emitAsync({
      eventType: EIDStackEvent.POP,
      id,
      currentLength: currentLength,
    });
    await events.emitAsync({ eventType: EIDStackEvent.REMOVED, id: id, currentLength: currentLength });
    data.value.delete(id);

  };

  return {
    push,
    pop,
    clear,
    events,
    items: outputItems,
  };
};
